<template>
  <div class="timeline-clicker">
    <div class="video-wrapper">
      <div v-if="!videoPlaying" class="play">
        {{ t('EventForm.timelineClicker.play') }}
      </div>
      <video ref="video" :src="src" data-testid="timeline-video" @click="togglePlayback"></video>
    </div>

    <div class="timeline-clicker__timeline" @click="moveToPosition">
      <div ref="timeline" class="timeline-clicker__timeline__line"></div>
      <!-- eslint-disable-next-line vue/no-restricted-v-bind -- playhead position tracks video time at runtime -->
      <div class="timeline-clicker__timeline__handle" :style="{ left: (videoCurrentTime / videoDuration) * 100 + '%' }"></div>
      <div class="timeline-clicker__timeline__timestamps">
        <template v-for="(marker, index) in agendaMarkers">
          <!-- eslint-disable vue/no-restricted-v-bind -- marker position is derived from agenda time at runtime -->
          <div
            v-if="marker.time !== null"
            :key="`agenda-${index}`"
            class="timeline-clicker__timeline__timestamps-marker timeline-clicker__timeline__timestamps-marker--top"
            :style="{ left: (marker.time / videoDuration) * 100 + '%' }"
            @click.stop="seekTo(marker.time)"
          >
            {{ Number(index) + 1 }}
          </div>
          <!-- eslint-enable vue/no-restricted-v-bind -->
        </template>
      </div>
    </div>
    <div class="timeline-clicker__controls">
      <div>
        <TextInput v-model="videoCurrentTimeInput" @keydown.enter="setVideoTime" />
      </div>
      <button class="btn-primary-color-s" data-testid="timeline-add-agenda" @click="createAgendaMarker">
        {{ t('EventForm.timelineClicker.addItem') }}
      </button>
    </div>
    <div class="timeline-clicker__list">
      <h3>{{ t('EventForm.timelineClicker.agendaLabel') }}</h3>
      <TableLight class="mt-6 mb-6 w-full" data-testid="timeline-agenda-table">
        <thead>
          <tr>
            <th class="text-start">Number</th>
            <th class="text-start">Time</th>
            <th class="text-start">Name</th>
            <th class="text-start">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in agendaMarkers" :key="'agenda-' + index">
            <td>{{ index + 1 }}</td>
            <td>
              <TextInput
                v-model="item.timeInput"
                class="list-time-input"
                :error="item.time === null ? t('EventForm.timelineClicker.invalidTime') : ''"
                data-testid="timeline-time-input"
                @blur="applyTimeInput(item)"
                @keydown.enter="applyTimeInput(item)"
              />
            </td>
            <td>
              <TextInput v-model="item.text" class="list-text" data-testid="timeline-name-input" @update:model-value="save" />
            </td>
            <td>
              <button class="btn-outline-color-s" data-testid="timeline-del-agenda" @click="deleteAgendaTimestamp(index)">
                <SvgIcon :path="mdiDelete" :size="24" />
              </button>
            </td>
          </tr>
        </tbody>
      </TableLight>
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiDelete } from '@mdi/js'
import { TextInput } from '@ramp106/omrjs-core-ui/deprecated'
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const toSeconds = (m: string): number | null => {
  const parts = m.trim().split(':')
  if (parts.length < 2 || parts.length > 3 || parts.some((p) => !/^\d+$/.test(p))) return null
  return parts.reduce((total, part) => total * 60 + parseInt(part), 0)
}
const padZero = (number: number): string => {
  if (number < 10) return `0${number}`
  else return `${number}`
}
const toMinutes = (timecode: number): string => {
  const hours = Math.floor(timecode / 60 / 60)
  const minutes = padZero(Math.floor(timecode / 60) % 60)
  const seconds = padZero(Math.floor(timecode % 60))
  return `${hours}:${minutes}:${seconds}`
}
type Marker = { time: number | null; timeInput: string; text: string }

const props = defineProps<{
  agendaTimestamps: string
  src: string
}>()

const emit = defineEmits<{
  (e: 'updateAgenda', value: string): void
}>()

const videoPlaying = ref(false)
const agendaMarkers = ref<Marker[]>([])
const videoDuration = ref(0)
const videoCurrentTime = ref(0)
const videoCurrentTimeInput = ref('0:00:00')
const video = ref<HTMLVideoElement>()
const timeline = ref<HTMLElement>()
const lastEmitted = ref<string | null>(null)

onMounted(() => {
  parseTimestamps()
  video.value?.addEventListener('loadedmetadata', () => {
    videoDuration.value = video.value?.duration || 0
  })
  video.value?.addEventListener('timeupdate', () => {
    videoCurrentTime.value = video.value?.currentTime || 0
    videoCurrentTimeInput.value = formatTime(video.value?.currentTime || 0)
  })
})

watch(
  () => props.agendaTimestamps,
  (newVal) => {
    if (newVal !== lastEmitted.value) parseTimestamps()
  },
)

function parseTimestamps() {
  agendaMarkers.value = JSON.parse(props.agendaTimestamps).map((b: { time: string; text: string }) => {
    const time = toSeconds(b.time) || 0
    return { time, timeInput: toMinutes(time), text: b.text }
  })
  sortMarkers()
}

function save() {
  lastEmitted.value = JSON.stringify(
    agendaMarkers.value.filter((b) => b.time !== null).map((b) => ({ time: toMinutes(b.time as number), text: b.text })),
  )
  emit('updateAgenda', lastEmitted.value)
}
function togglePlayback() {
  if (videoPlaying.value) {
    pauseVideo()
  } else {
    playVideo()
  }
}
function playVideo() {
  video.value?.play()
  videoPlaying.value = true
}

function pauseVideo() {
  video.value?.pause()
  videoPlaying.value = false
}

function moveToPosition(e: { offsetX: number }) {
  if (!timeline.value) return

  const percent = e.offsetX / timeline.value?.offsetWidth
  const videoRef = video.value
  if (videoRef) videoRef.currentTime = percent * videoDuration.value
}

function seekTo(time: number) {
  if (video.value) video.value.currentTime = time
}

function createAgendaMarker() {
  const time = video.value?.currentTime || 0
  agendaMarkers.value = agendaMarkers.value.concat([{ time, timeInput: toMinutes(time), text: '' }])
  sortMarkers()
  save()
}
function deleteAgendaTimestamp(index: number) {
  agendaMarkers.value = agendaMarkers.value.filter((_, i) => i !== index)
  save()
}
function applyTimeInput(item: Marker) {
  const parsed = toSeconds(item.timeInput)
  if (parsed === null) {
    item.time = null
  } else {
    item.time = videoDuration.value > 0 ? Math.min(parsed, videoDuration.value) : parsed
    item.timeInput = toMinutes(item.time)
    sortMarkers()
  }
  save()
}
function sortMarkers() {
  agendaMarkers.value = agendaMarkers.value.sort((a, b) => (a.time ?? Infinity) - (b.time ?? Infinity))
}
function formatTime(timecode: number): string {
  return toMinutes(timecode)
}

function setVideoTime() {
  const timecode = toSeconds(videoCurrentTimeInput.value)
  if (timecode !== null && video.value) video.value.currentTime = timecode
}
</script>
<style scoped>
.video-wrapper {
  position: relative;
}

.play {
  position: absolute;
  z-index: 1;
  color: white;
  background: black;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  padding: calc(var(--spacing) * 2.5);
  border-radius: var(--radius-md);
  opacity: 0.8;
  pointer-events: none;
}

video {
  cursor: pointer;
  width: 100%;
  position: relative;
}

.timeline-clicker__timeline {
  width: 100%;
  position: relative;
  margin-top: calc(var(--spacing) * 5);
  padding-top: calc(var(--spacing) * 2.5);
  padding-bottom: calc(var(--spacing) * 2.5);
  margin-bottom: calc(var(--spacing) * 7.5);
}

.timeline-clicker__timeline__line {
  width: 100%;
  height: 3px;
  background: black;
}

.timeline-clicker__timeline__handle {
  pointer-events: none;
  width: 10px;
  height: 17px;
  background: white;
  z-index: 5;
  position: absolute;
  top: 11px;
  transform: translate(-50%, -50%);
  border: 3px solid black;
  transition: all var(--motion-duration-fast) var(--motion-easing-standard);
}

.timeline-clicker__timeline__timestamps {
  height: 30px;
  position: absolute;
  width: 100%;
}

.timeline-clicker__timeline__timestamps-marker {
  position: absolute;
  background: white;
  color: black;
  border: solid 3px black;
  border-radius: 50%;
  width: 30px;
  text-align: center;
  z-index: 1;
  transition: all var(--motion-duration-fast) var(--motion-easing-standard);
  cursor: pointer;
  user-select: none;
}

.timeline-clicker__timeline__timestamps-marker--top {
  top: -100%;
  transform-origin: bottom center;
  transform: scale(0.7) translateX(-50%) translateY(-9px);
}

.timeline-clicker__timeline__timestamps-marker--top:hover {
  transform: scale(1) translateX(-10px) translateY(-7px);
}

.timeline-clicker__timeline__timestamps-marker--top::before {
  transition: all var(--motion-duration-fast) var(--motion-easing-standard);
  width: 0px;
  height: 0px;
  position: absolute;
  transform: translate(0%, -90%);
  left: 0;
  top: calc(100% + 18px);
  z-index: 0;
  border: 12px solid transparent;
  content: '';
  border-top-color: black;
  pointer-events: none;
}

.timeline-clicker__controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.timeline-clicker__list {
  margin-top: calc(var(--spacing) * 2.5);
}

.timeline-clicker__list li {
  display: grid;
  grid-template-columns: 50px 100px auto 60px;
  grid-template-areas: 'number time text delete';
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  margin-bottom: calc(var(--spacing) * 0.75);
}

.timeline-clicker__list .list-number {
  grid-area: number;
  padding-top: calc(var(--spacing) * 1);
  margin-top: calc(var(--spacing) * 1);
  text-align: center;
  cursor: pointer;
  user-select: none;
}

.timeline-clicker__list .list-time {
  grid-area: time;
  padding-top: calc(var(--spacing) * 1);
  margin-top: calc(var(--spacing) * 1);
  cursor: pointer;
  user-select: none;
  text-align: center;
}

.timeline-clicker__list .list-text {
  grid-area: text;
  padding-top: 0;
}

.timeline-clicker__list .list-delete {
  grid-area: delete;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
